import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  isExternalOnlyCarrier,
  type Carrier,
  type CarrierVisibility,
} from "@/api/carriers";
import { Plus, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { CarrierServicesSection } from "./components/carrier-services-section";
import { CarrierDropoffLocationsSection } from "./components/carrier-dropoff-locations-section";
import { CarrierFeeSection } from "./carrier-fee-section";
import { ShippingPricesSection } from "./shipping-prices-section";
import { ExternalCarrierCoverageSection } from "./external-carrier-coverage-section";

const API_URL = import.meta.env.VITE_API_URL;

interface EditCarrierDialogProps {
  open: boolean;
  onClose: () => void;
  carrier: Carrier;
  /** Saved purchasing options can refresh without resetting the carrier form draft. */
  channels?: Carrier["channels"];
}

export function EditCarrierDialog({
  open,
  onClose,
  carrier,
  channels = carrier.channels,
}: EditCarrierDialogProps) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [trackingUrl, setTrackingUrl] = useState<string>("");
  const [trackingPatterns, setTrackingPatterns] = useState<string[]>([]);
  const [newPatternInput, setNewPatternInput] = useState<string>("");
  const [visibility, setVisibility] = useState<CarrierVisibility>("PUBLIC");
  const [defaultEnabled, setDefaultEnabled] = useState(false);
  const [customerCanToggle, setCustomerCanToggle] = useState(true);
  const externalOnly = isExternalOnlyCarrier({ channels });

  // Load initial data from carrier
  useEffect(() => {
    if (carrier) {
      setVisibility(carrier.visibility ?? "PUBLIC");
      setDefaultEnabled(carrier.defaultEnabled ?? false);
      setCustomerCanToggle(carrier.customerCanToggle ?? true);
      setLogoUrl(carrier.logoUrl ?? "");
      setTrackingUrl((carrier.metadata?.trackingUrl as string) ?? "");
      setTrackingPatterns(
        (carrier.metadata?.trackingNumberPatterns as string[]) ?? []
      );
    }
  }, [carrier]);

  const handleAddPattern = () => {
    if (newPatternInput && !trackingPatterns.includes(newPatternInput)) {
      setTrackingPatterns([...trackingPatterns, newPatternInput]);
      setNewPatternInput("");
    }
  };

  const handleRemovePattern = (index: number) => {
    setTrackingPatterns(trackingPatterns.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      // How EZX procures from this carrier is a SupplyChannel, not a field on
      // the carrier — a carrier can be reachable several ways at once. Channels
      // have their own explicitly saved section in this dialog.
      await axios.patch(
        `${API_URL}/api/v2/carriers/${carrier.id}`,
        {
          visibility,
          defaultEnabled,
          customerCanToggle,
          logoUrl: logoUrl ?? null,
          metadata: {
            ...carrier.metadata,
            trackingUrl: trackingUrl ?? undefined,
            trackingNumberPatterns:
              trackingPatterns.length > 0 ? trackingPatterns : undefined,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Carrier configuration updated successfully.");

      void queryClient.invalidateQueries({ queryKey: ["carriers"] });
      onClose();
    } catch (error) {
      console.error("Failed to update carrier:", error);
      toast.error("Failed to update carrier configuration.");
    } finally {
      setIsLoading(false);
    }
  };

  const restricted = visibility === "RESTRICTED";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[calc(100vh-2rem)] w-[min(900px,calc(100vw-2rem))] flex-col gap-0 overflow-hidden sm:max-w-[900px]">
        <DialogHeader className="shrink-0 pb-4">
          <DialogTitle>Edit Carrier Configuration</DialogTitle>
          <DialogDescription>
            Configure supply, branding, tracking, services and visibility for{" "}
            {carrier.name}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-2">
          <ShippingPricesSection
            key={carrier.id}
            channels={channels ?? []}
            carrierCode={carrier.code}
            carrierId={carrier.id}
            carrierName={carrier.name}
          />
          <CarrierFeeSection
            key={carrier.id}
            carrierId={carrier.id}
            carrierName={carrier.name}
          />
          {/* Logo URL */}
          <div className="space-y-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input
              id="logoUrl"
              type="url"
              placeholder="https://example.com/logo.png"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              URL to the carrier's logo image
            </p>
          </div>

          {/* Tracking Configuration */}
          <div className="space-y-4 border rounded-lg p-4">
            <Label className="text-sm font-medium">
              Tracking Configuration
            </Label>

            {/* Tracking URL */}
            <div className="space-y-2">
              <Label
                htmlFor="trackingUrl"
                className="text-sm text-muted-foreground"
              >
                Tracking URL
              </Label>
              <Input
                id="trackingUrl"
                placeholder="https://carrier.com/track?num={trackingNumber}"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Use {"{trackingNumber}"} as placeholder for the tracking number
              </p>
            </div>

            {/* Tracking Number Patterns */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Tracking Number Patterns
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="^1Z[A-Z0-9]{16}$"
                  value={newPatternInput}
                  onChange={(e) => setNewPatternInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddPattern();
                    }
                  }}
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddPattern}
                  disabled={!newPatternInput}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {trackingPatterns.length === 0 ? (
                  <span className="text-xs text-muted-foreground">
                    No patterns configured
                  </span>
                ) : (
                  trackingPatterns.map((pattern: string, i: number) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="font-mono text-xs pl-2 pr-1 py-0.5"
                    >
                      {pattern}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-3 w-3 p-0 ml-1"
                        onClick={() => handleRemovePattern(i)}
                      >
                        <X className="h-2 w-2" />
                      </Button>
                    </Badge>
                  ))
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Regex patterns to identify tracking numbers from this carrier
              </p>
            </div>
          </div>

          <CarrierServicesSection
            carrierId={carrier.id}
            carrierName={carrier.name}
            carrierCode={carrier.code}
            channels={channels}
          />

          {externalOnly && (
            <ExternalCarrierCoverageSection carrierId={carrier.id} />
          )}

          <CarrierDropoffLocationsSection
            carrierId={carrier.id}
            carrierName={carrier.name}
          />

          <div className="space-y-2 border-t pt-4">
            <Label>Visibility</Label>
            <div className="flex items-start gap-2">
              <Checkbox
                id="carrier-restricted"
                checked={restricted}
                onCheckedChange={(v) =>
                  setVisibility(v === true ? "RESTRICTED" : "PUBLIC")
                }
              />
              <div className="grid gap-0.5">
                <label
                  htmlFor="carrier-restricted"
                  className="text-sm font-medium leading-none"
                >
                  Restrict to granted customers only
                </label>
                <p className="text-xs text-muted-foreground">
                  {restricted
                    ? "Only customers you explicitly grant will see or be able to buy from this carrier."
                    : "Public: every customer can discover and enable this carrier."}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="carrier-default-on"
                checked={defaultEnabled}
                disabled={!customerCanToggle}
                onCheckedChange={(v) => setDefaultEnabled(v === true)}
              />
              <div className="grid gap-0.5">
                <label
                  htmlFor="carrier-default-on"
                  className="text-sm font-medium leading-none"
                >
                  On by default for new customers
                </label>
                <p className="text-xs text-muted-foreground">
                  Switched on the moment a customer signs up — they don&apos;t
                  have to add it. Existing customers are not affected.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="carrier-locked"
                checked={!customerCanToggle}
                onCheckedChange={(v) => setCustomerCanToggle(v !== true)}
              />
              <div className="grid gap-0.5">
                <label
                  htmlFor="carrier-locked"
                  className="text-sm font-medium leading-none"
                >
                  Customers can&apos;t turn this off
                </label>
                <p className="text-xs text-muted-foreground">
                  {customerCanToggle
                    ? "Customers can switch this carrier on and off themselves."
                    : "Mandatory. Saving will switch this carrier ON for every existing customer, including any who had turned it off. While locked, not even EZX can revoke it for a single customer — unlock it first."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
